import { rewriteEntityTerms } from '@/lib/terminology/entity-terms'

const API_ERROR_PREFIX = /^API Error\s+\d+:\s*/i

type ErrorData = Record<string, unknown>

const extractMessageFromData = (data: unknown): string | undefined => {
  if (!data) return undefined
  if (typeof data === 'string') return data
  if (Array.isArray(data)) {
    return data.length > 0 ? extractMessageFromData(data[0]) : undefined
  }
  if (typeof data !== 'object') return undefined

  const dataObj = data as ErrorData
  if (typeof dataObj.message === 'string') return dataObj.message
  if (Array.isArray(dataObj.message)) {
    return dataObj.message.filter(Boolean).join(', ')
  }
  if (Array.isArray(dataObj.errors)) {
    const nestedMessage = extractMessageFromData(dataObj.errors)
    if (nestedMessage) return nestedMessage
  }
  if (typeof dataObj.error === 'string') return dataObj.error
  if (typeof dataObj.detail === 'string') return dataObj.detail

  const nestedError = dataObj.error as ErrorData | undefined
  if (nestedError && typeof nestedError.message === 'string') {
    return nestedError.message
  }

  return undefined
}

const extractStatusFromData = (data: unknown): number | undefined => {
  if (!data || typeof data !== 'object') return undefined
  const dataObj = data as ErrorData

  const status = dataObj.status ?? dataObj.statusCode
  return typeof status === 'number' ? status : undefined
}

export const parseApiErrorText = (
  errorText: string,
  fallback = 'An error occurred',
): { message: string; data?: unknown } => {
  const trimmed = errorText.trim()
  if (!trimmed) {
    return { message: fallback }
  }

  const payload = trimmed.replace(API_ERROR_PREFIX, '').trim()
  if (!payload) {
    return { message: fallback }
  }

  if (payload.startsWith('{') || payload.startsWith('[')) {
    try {
      const parsed = JSON.parse(payload)
      const extracted = extractMessageFromData(parsed)
      return { message: extracted ?? fallback, data: parsed }
    } catch {
      return { message: payload, data: payload }
    }
  }

  return { message: payload, data: payload }
}

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback

  if (typeof error === 'string') {
    return parseApiErrorText(error, fallback).message
  }

  const errorObj = error as {
    message?: unknown
    data?: unknown
    response?: { data?: unknown }
  }

  const dataMessage = extractMessageFromData(
    errorObj.data ?? errorObj.response?.data,
  )
  if (dataMessage) return dataMessage

  if (typeof errorObj.message === 'string') {
    return parseApiErrorText(errorObj.message, fallback).message
  }

  const objectMessage = extractMessageFromData(errorObj)
  return objectMessage ?? fallback
}

// API messages say "entity"; users see the org's configured term
export const getErrorMessage = (
  error: unknown,
  fallback = 'An error occurred',
): string => rewriteEntityTerms(resolveErrorMessage(error, fallback))

export const getErrorStatus = (error: unknown): number | undefined => {
  if (!error) return undefined

  if (typeof error === 'object') {
    const errorObj = error as {
      status?: unknown
      statusCode?: unknown
      response?: { status?: unknown; data?: unknown }
      data?: unknown
    }

    const directStatus =
      errorObj.status ?? errorObj.statusCode ?? errorObj.response?.status
    if (typeof directStatus === 'number') {
      return directStatus
    }

    const dataStatus = extractStatusFromData(
      errorObj.data ?? errorObj.response?.data,
    )
    if (typeof dataStatus === 'number') {
      return dataStatus
    }
  }

  const message =
    typeof error === 'string' ? error : (error as Error | undefined)?.message
  if (typeof message !== 'string') return undefined

  const match = message.match(/API Error\s+(\d+)/i)
  if (match) {
    const status = Number(match[1])
    return Number.isNaN(status) ? undefined : status
  }

  return undefined
}

export const isAuthenticationAvailabilityError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  const errorObj = error as {
    data?: unknown
    response?: { data?: unknown }
  }
  const data = errorObj.data ?? errorObj.response?.data
  if (!data || typeof data !== 'object') return false

  const payload = data as ErrorData
  return (
    payload.code === 'AUTH_PROVIDER_UNAVAILABLE' ||
    payload.reason === 'transient'
  )
}

export const isAICreditsExhaustedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  const errorObj = error as {
    code?: unknown
    data?: unknown
    response?: { data?: unknown }
  }
  const data = errorObj.data ?? errorObj.response?.data ?? errorObj
  return (
    !!data &&
    typeof data === 'object' &&
    (data as ErrorData).code === 'AI_CREDITS_EXHAUSTED'
  )
}
